/**
 * tRPC link that detects expired sessions and redirects to sign-in.
 *
 * authFetch.ts handles the silent refresh attempt — if the refresh fails,
 * it throws "Session expired" which arrives here. This link fires a PostHog
 * event (once per session) and redirects the user to sign-in.
 */
import { observable } from "@trpc/server/observable";
import type { AnyRouter } from "@trpc/server/unstable-core-do-not-import";
import type { TRPCClientError, TRPCLink } from "@trpc/client";
import posthog from "posthog-js";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { SS_SESSION_EXPIRED } from "@/lib/storage-keys";
import { SESSION_EXPIRED_MESSAGE } from "@/lib/trpc/sessionExpiredMessage";

let redirectScheduled = false;

export function sessionExpiredLink<TRouter extends AnyRouter = AnyRouter>(): TRPCLink<TRouter> {
  return () => {
    return ({ op, next }) => {
      return observable((observer) => {
        const subscription = next(op).subscribe({
          next: (value) => observer.next(value),
          error: (err: TRPCClientError<TRouter>) => {
            try {
              if (
                typeof window !== "undefined" &&
                err?.message?.includes?.(SESSION_EXPIRED_MESSAGE)
              ) {
                if (
                  process.env.NEXT_PUBLIC_POSTHOG_KEY &&
                  !sessionStorage.getItem(SS_SESSION_EXPIRED)
                ) {
                  try {
                    posthog.capture(ANALYTICS_EVENTS.USER_SESSION_EXPIRED);
                    sessionStorage.setItem(SS_SESSION_EXPIRED, "1");
                  } catch {
                    /* benign: best-effort analytics and storage access may fail */
                  }
                }

                if (!redirectScheduled) {
                  redirectScheduled = true;
                  // pathname+search+hash — never the full href, so the value
                  // stays same-origin.
                  const returnTo = encodeURIComponent(
                    window.location.pathname + window.location.search + window.location.hash,
                  );
                  setTimeout(() => {
                    window.location.href = `/sign-in?redirect_url=${returnTo}`;
                  }, 200);
                }
              }
            } catch {
              /* benign: error handler protection to prevent observer chain breakage */
            }
            observer.error(err);
          },
          complete: () => observer.complete(),
        });
        return () => subscription.unsubscribe();
      });
    };
  };
}

/** Test-only: reset the redirect guard. */
export function _resetRedirectGuardForTesting() {
  redirectScheduled = false;
}
